import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, Search } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import { cn } from '@/shared/lib/cn';
import {
  listRecepciones,
  type RecepcionRow,
  type RecepcionStatusFilter,
} from './api';
import { RecepcionTable } from './components/RecepcionTable';
import { DetalleRecepcionDialog } from './components/DetalleRecepcionDialog';
import { useActiveCountry } from '@/shared/countries/ActiveCountryProvider';
import { useRealtimeInvalidate } from '@/shared/realtime/useRealtimeInvalidate';

/**
 * Recepción main page — receipt-tracking browser. Lists recepciones
 * by status and opens per-row detail dialogs for receive/finalize/
 * anular actions.
 *
 * Recepciones are auto-created when a compra with stock-impacting
 * items is saved — no manual create flow surfaced here. The
 * `CrearRecepcionDialog` component is kept on disk in case it's
 * needed later for legacy compras that didn't auto-generate one,
 * but the trigger has been removed from the UI.
 *
 * Inventory moves (PorIngresar → Disponible) are NOT handled here —
 * that's a manual action in the Inventario module.
 */
export function RecepcionPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { config: activeCountry } = useActiveCountry();
  const [status, setStatus] = useState<RecepcionStatusFilter>('Pendiente');
  const [search, setSearch] = useState('');
  const [viewing, setViewing] = useState<RecepcionRow | null>(null);

  const recepcionesQ = useQuery({
    // Country in the key — receptions are country-scoped via purchase
    // headers.
    queryKey: ['recepcion', 'list', activeCountry.code, status] as const,
    queryFn: () => listRecepciones({ status }),
    staleTime: 30_000,
  });

  // Admin "Recibir" / "Finalizar" actions push the row's status — the
  // status-filtered gallery refreshes instantly without manual refetch.
  useRealtimeInvalidate('receptions', ['recepcion']);

  const allRows = recepcionesQ.data?.rows ?? [];
  const q = search.trim().toLowerCase();
  const filtered = q
    ? allRows.filter((r) => {
        const hay = [
          r.id,
          r.fields.IDCompra_RR ?? '',
          r.compra?.proveedor ?? '',
          r.compra?.nroFactura ?? '',
        ]
          .join(' ')
          .toLowerCase();
        return hay.includes(q);
      })
    : allRows;

  return (
    <div className="flex h-full flex-col gap-4">
      {/* Toolbar — DOM order matches desktop (Back, Title, Status,
          Search, Refresh). On mobile we re-arrange via CSS `order` +
          a `basis-full` break so the title row gets [Back · Title ·
          Refresh] and the status select + search drop onto a second
          row. */}
      <div className="flex shrink-0 flex-wrap items-center gap-3">
        {/* Back to Inventario — Recepción is conceptually a sub-screen
            of Inventario but lives at its own URL. The navbar already
            highlights Inventario via `activePaths`, so this button is
            the symmetrical "leave the sub-screen" affordance. */}
        <Button
          size="icon"
          className="h-10 w-10 shrink-0 rounded-full bg-foreground text-background hover:bg-foreground/90"
          onClick={() => navigate('/inventario')}
          aria-label="Volver a Inventario"
          title="Volver a Inventario"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden />
        </Button>
        <h1 className="mr-auto text-2xl font-semibold tracking-tight text-foreground md:text-3xl">
          Recepción
        </h1>
        <div className="order-3 w-32 shrink-0 sm:order-none sm:w-52">
          <Select
            value={status}
            onValueChange={(v) => setStatus(v as RecepcionStatusFilter)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los estados</SelectItem>
              <SelectItem value="Pendiente">Pendientes</SelectItem>
              <SelectItem value="Recibido">Recibidas</SelectItem>
              <SelectItem value="Anulado">Anuladas</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {/* Forces the status select + search onto a new row on mobile only. */}
        <div className="order-2 basis-full sm:hidden" aria-hidden />
        <div className="relative order-4 min-w-[10rem] flex-1 sm:order-none sm:w-64 sm:flex-none">
          <Search
            className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <Input
            variant="filled"
            placeholder="Nº, proveedor, factura"
            className="pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Buscar"
          />
        </div>
        <Button
          variant="outline"
          size="icon"
          className="order-1 h-12 w-12 shrink-0 sm:order-none"
          onClick={() => qc.invalidateQueries({ queryKey: ['recepcion'] })}
          aria-label="Actualizar"
          title="Actualizar"
        >
          <RefreshCw
            className={cn('h-4 w-4', recepcionesQ.isFetching && 'animate-spin')}
            aria-hidden
          />
        </Button>
      </div>

      <RecepcionTable
        rows={filtered}
        loading={recepcionesQ.isPending}
        error={recepcionesQ.isError}
        searchActive={Boolean(q)}
        onView={(row) => setViewing(row)}
        onRetry={() => recepcionesQ.refetch()}
      />

      {viewing && (
        <DetalleRecepcionDialog
          summary={viewing}
          onClose={() => setViewing(null)}
        />
      )}
    </div>
  );
}
